import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Group,
  MultiSelect,
  NumberInput,
  Paper,
  RangeSlider,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useCreatePlaylist, usePreviewPlaylist } from '~/api/mutations'
import { useLibraryFolders } from '~/api/queries'
import type { Track } from '~/api/types'
import { TrackTable } from '~/components/blocks/TrackTable/TrackTable'
import { TextInput } from '~/components/primitives/form'
import { formatDuration } from '~/lib/format'

export const Route = createFileRoute('/_app/playlists/generate')({
  component: GeneratePlaylistPage,
})

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  comment: z.string().optional(),
  limit: z.number().int().min(1).max(500),
  danceability: z.tuple([z.number(), z.number()]),
  aggression: z.tuple([z.number(), z.number()]),
  happiness: z.tuple([z.number(), z.number()]),
  party: z.tuple([z.number(), z.number()]),
  relaxation: z.tuple([z.number(), z.number()]),
  sadness: z.tuple([z.number(), z.number()]),
  energy: z.tuple([z.number(), z.number()]),
  minTempo: z.number().min(0).optional(),
  maxTempo: z.number().min(0).optional(),
  keyName: z.string().optional(),
  excludeFolderIds: z.array(z.string()).optional(),
  maxSizeMB: z.number().min(0).optional(),
})

type FormData = z.infer<typeof schema>

const MOOD_SLIDERS = [
  { name: 'danceability', label: 'Danceability' },
  { name: 'aggression', label: 'Aggression' },
  { name: 'happiness', label: 'Happiness' },
  { name: 'party', label: 'Party' },
  { name: 'relaxation', label: 'Relaxation' },
  { name: 'sadness', label: 'Sadness' },
] as const

const KEY_OPTIONS = (() => {
  const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
  const modes = ['major', 'minor']
  return notes.flatMap((note) =>
    modes.map((mode) => ({
      value: `${note} ${mode}`,
      label: `${note} ${mode}`,
    }))
  )
})()

function isNarrowed(range: [number, number]): boolean {
  return range[0] > 0 || range[1] < 1
}

function buildCriteriaBody(data: FormData) {
  const body: Record<string, unknown> = {
    limit: data.limit,
  }

  for (const { name } of MOOD_SLIDERS) {
    const range = data[name] as [number, number]
    if (isNarrowed(range)) {
      body[`min${name[0].toUpperCase()}${name.slice(1)}`] = range[0]
      body[`max${name[0].toUpperCase()}${name.slice(1)}`] = range[1]
    }
  }

  const energy = data.energy
  if (isNarrowed(energy)) {
    body.minEnergy = energy[0]
    body.maxEnergy = energy[1]
  }

  if (data.minTempo !== undefined) body.minTempo = data.minTempo
  if (data.maxTempo !== undefined) body.maxTempo = data.maxTempo
  if (data.keyName) body.keyName = data.keyName
  if (data.excludeFolderIds && data.excludeFolderIds.length > 0)
    body.excludeFolderIds = data.excludeFolderIds.map(Number)
  if (data.maxSizeMB !== undefined) body.maxSize = data.maxSizeMB * 1_000_000

  return body
}

function GeneratePlaylistPage() {
  const navigate = useNavigate()
  const previewPlaylist = usePreviewPlaylist()
  const createPlaylist = useCreatePlaylist()
  const { data: foldersData } = useLibraryFolders()
  const folderOptions = (foldersData?.data ?? []).map((f) => ({
    value: String(f.id),
    label: f.name,
  }))

  const [previewTracks, setPreviewTracks] = useState<Track[] | null>(null)
  const [savedFormData, setSavedFormData] = useState<FormData | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      comment: '',
      limit: 50,
      danceability: [0, 1],
      aggression: [0, 1],
      happiness: [0, 1],
      party: [0, 1],
      relaxation: [0, 1],
      sadness: [0, 1],
      energy: [0, 1],
      minTempo: undefined,
      maxTempo: undefined,
      keyName: undefined,
      excludeFolderIds: [],
      maxSizeMB: undefined,
    },
  })

  const onPreview = async (data: FormData) => {
    const body = buildCriteriaBody(data)
    try {
      const result = await previewPlaylist.mutateAsync(
        body as Parameters<typeof previewPlaylist.mutateAsync>[0]
      )
      setSavedFormData(data)
      setPreviewTracks(result.tracks)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to preview playlist.'
      notifications.show({ title: 'Error', message, color: 'red' })
    }
  }

  const onSave = async () => {
    if (!savedFormData || !previewTracks) return
    setIsSaving(true)
    try {
      const trackIds = previewTracks.map((t) => t.id)
      const result = await createPlaylist.mutateAsync({
        name: savedFormData.name,
        comment: savedFormData.comment || undefined,
        trackIds,
      })
      notifications.show({ title: 'Playlist created', message: savedFormData.name, color: 'green' })
      navigate({
        to: '/playlists/$playlistId',
        params: { playlistId: String((result as { id: number }).id) },
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create playlist.'
      notifications.show({ title: 'Error', message, color: 'red' })
    } finally {
      setIsSaving(false)
    }
  }

  const onBackToEdit = () => {
    setPreviewTracks(null)
  }

  if (previewTracks) {
    const totalDuration = previewTracks.reduce((sum, t) => sum + t.duration, 0)
    return (
      <Stack gap="md">
        <Title order={2}>
          Preview: {previewTracks.length} tracks, {formatDuration(totalDuration)}
        </Title>
        <Group>
          <Button onClick={onSave} loading={isSaving}>
            Save Playlist
          </Button>
          <Button variant="default" onClick={onBackToEdit} disabled={isSaving}>
            Back to Edit
          </Button>
        </Group>
        <TrackTable tracks={previewTracks} />
      </Stack>
    )
  }

  return (
    <Stack gap="md">
      <Title order={2}>Generate Playlist</Title>
      <Paper withBorder p="lg" maw={600}>
        <form onSubmit={handleSubmit(onPreview)}>
          <Stack gap="md">
            <TextInput
              name="name"
              control={control}
              label="Name"
              placeholder="My Generated Playlist"
              required
            />
            <TextInput
              name="comment"
              control={control}
              label="Description"
              placeholder="Optional description"
            />

            <Controller
              name="limit"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Track limit"
                  min={1}
                  max={500}
                  value={field.value}
                  onChange={(v) => field.onChange(typeof v === 'number' ? v : 50)}
                />
              )}
            />

            <Title order={4} mt="sm">
              Mood Ranges
            </Title>
            <Text size="xs" c="dimmed">
              Narrow the sliders to filter by mood dimension (0–1)
            </Text>

            {MOOD_SLIDERS.map(({ name, label }) => (
              <Controller
                key={name}
                name={name}
                control={control}
                render={({ field }) => (
                  <div>
                    <Text size="sm" fw={500} mb={4}>
                      {label}
                    </Text>
                    <RangeSlider
                      min={0}
                      max={1}
                      step={0.01}
                      minRange={0.01}
                      value={field.value as [number, number]}
                      onChange={field.onChange}
                      label={(v) => v.toFixed(2)}
                    />
                  </div>
                )}
              />
            ))}

            <Title order={4} mt="sm">
              Audio Criteria
            </Title>

            <Controller
              name="energy"
              control={control}
              render={({ field }) => (
                <div>
                  <Text size="sm" fw={500} mb={4}>
                    Energy
                  </Text>
                  <RangeSlider
                    min={0}
                    max={1}
                    step={0.01}
                    minRange={0.01}
                    value={field.value as [number, number]}
                    onChange={field.onChange}
                    label={(v) => v.toFixed(2)}
                  />
                </div>
              )}
            />

            <Group grow>
              <Controller
                name="minTempo"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label="Min BPM"
                    min={0}
                    max={300}
                    placeholder="Any"
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(typeof v === 'number' ? v : undefined)}
                  />
                )}
              />
              <Controller
                name="maxTempo"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label="Max BPM"
                    min={0}
                    max={300}
                    placeholder="Any"
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(typeof v === 'number' ? v : undefined)}
                  />
                )}
              />
            </Group>

            <Controller
              name="keyName"
              control={control}
              render={({ field }) => (
                <Select
                  label="Key"
                  placeholder="Any key"
                  clearable
                  searchable
                  data={KEY_OPTIONS}
                  value={field.value ?? null}
                  onChange={(v) => field.onChange(v ?? undefined)}
                />
              )}
            />

            <Title order={4} mt="sm">
              Filters
            </Title>

            <Controller
              name="excludeFolderIds"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label="Exclude folders"
                  placeholder="None (include all)"
                  data={folderOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  clearable
                  searchable
                />
              )}
            />

            <Controller
              name="maxSizeMB"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Max file size (MB)"
                  min={0}
                  placeholder="Any"
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(typeof v === 'number' ? v : undefined)}
                />
              )}
            />

            <Button type="submit" loading={isSubmitting} mt="md">
              Preview
            </Button>
          </Stack>
        </form>
      </Paper>
    </Stack>
  )
}
